<template>
  <div
    ref="rootEl"
    :class="{ 'candidates-manager' : true, 'with-event': showEventDetails }"
  >
    <grid
      v-if="!showTeams"
      ref="candidatesGrid"
      :show-header="true"
      :list-url="gridListUrl"
      loading-message="Loading candidates ..."
      :add-checkmarks="checkmarks"
      :edit-action-as-link="false"
      :new-header-action-emit="true"
      :row-class-fn="gridRowClassFn"
      :header-class-fn="gridHeaderClassFn"
      @links-loaded="gridLoadedLinks"
      @meta-loaded="gridMetaLoaded"
      @item-action="onItemAction"
      @header-action="onHeaderAction"
    >
      <template
        v-if="showEventDetails && eventDetails"
        #before-header
      >
        <div class="card-header">
          Candidates for event
        </div>
        <div class="card-body">
          <div class="event-header-table">
            <div class="r">
              <div class="c1">Event:</div>
              <div class="c2"><a :href="eventDetails.url">{{ eventDetails.number }} {{ eventDetails.name }}</a></div>
            </div>
            <div
              v-if="hasAdditionalEventDetails"
              class="r"
            >
              <div class="c1">Details:</div>
              <div class="c2">
                <a
                  :href="eventDetails.detailsUrl || undefined"
                  target="_blank"
                >
                  View additional information at {{ eventDetails.detailsUrlHost }}
                </a>
              </div>
            </div>
            <div class="r">
              <div class="c1">Dates:</div>
              <div class="c2">{{ eventDetails.dateRange }}</div>
            </div>
            <div
              v-if="hasEventLanguages"
              class="r"
            >
              <div class="c1">Languages:</div>
              <div class="c2">{{ eventDetails.languages }}</div>
            </div>
          </div>
        </div>
      </template>
      <template #custom-cell="cellObj">
        <template v-if="cellObj.column.name == 'ministry_iban_status'">
          <span
            :class="ibanStatusClass('per-diem', cellObj.item)"
            :title="ibanStatus('per-diem', cellObj.item)"
          /> | <span
            :class="ibanStatusClass('transport', cellObj.item)"
            :title="ibanStatus('transport', cellObj.item)"
          />
        </template>
        <a
          v-else-if="cellObj.item.meta && cellObj.item.meta.ghost"
          href="#"
          data-bs-toggle="popover"
          data-bs-html="true"
          data-bs-title="Ghost candidate"
          :data-bs-content="ghostDetails(cellObj.item)"
          @click.prevent
        >
          <span class="cursor-pointer glyphicons warning text-warning" />
        </a>
      </template>
    </grid>

    <teams-grid
      v-if="showTeams"
      ref="teamsGrid"
      :list-url="teamsListUrlProp"
      @meta-loaded="gridMetaLoaded"
    >
      <template
        v-if="showEventDetails && eventDetails"
        #grid-before-header
      >
        <div class="card-header">
          Teams for event
        </div>
        <div class="card-body">
          <div class="event-header-table">
            <div class="r">
              <div class="c1">Event:</div>
              <div class="c2"><a :href="eventDetails.url">{{ eventDetails.number }} {{ eventDetails.name }}</a></div>
            </div>
            <div
              v-if="hasAdditionalEventDetails"
              class="r"
            >
              <div class="c1">Details:</div>
              <div class="c2">
                <a
                  :href="eventDetails.detailsUrl || undefined"
                  target="_blank"
                >
                  View additional information at {{ eventDetails.detailsUrlHost }}
                </a>
              </div>
            </div>
            <div class="r">
              <div class="c1">Dates:</div>
              <div class="c2">{{ eventDetails.dateRange }}</div>
            </div>
            <div
              v-if="hasEventLanguages"
              class="r"
            >
              <div class="c1">Languages:</div>
              <div class="c2">{{ eventDetails.languages }}</div>
            </div>
          </div>
        </div>
      </template>
    </teams-grid>

    <teleport
      to="#vue-portal-target"
      append
    >
      <candidate-modal
        ref="candidateModal"
        :event="candidateModalProps.event"
        :modal-title="candidateModalProps.modalTitle"
        :search-url="candidateSearchUrl"
        :show-search="candidateModalProps.showSearch"
        @success="candidateModalSuccess"
      />
      <candidate-details-modal ref="candidateDetailsModal" />
      <xlsx-upload-modal
        :upload-url="uploadCandidatesUrlProp"
        :template-url="uploadTemplateUrlProp"
        ref="uploadCandidatesModal"
      />
      <remote-modal
        ref="selectOpenCandidatesModal"
        :modal-form="true"
        @form-success="onApprovedOpenCandidates"
      />
    </teleport>
  </div>
</template>
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue"

import axios from "axios"
import Rails from "@rails/ujs"
import { Popover } from "bootstrap"

import Grid from "./grid.vue"
import CandidateModal from "./candidate-modal.vue"
import CandidateDetailsModal from "./candidate-details-modal.vue"
import TeamsGrid from "./teams-grid.vue"
import XlsxUploadModal from "./xlsx-upload-modal.vue"
import RemoteModal from "@/js/vue-components/remote-modal.vue"

import type { ButtonAction, GridHeaderAction } from "@/js/vue-components/types"
import { getValueStrNullable, isNonEmptyString, isPresent } from "../js-utils"

interface EventDetails {
  id: number | null;
  url: string;
  number: string;
  name: string;
  detailsUrl: string | null;
  detailsUrlHost: string | null;
  dateRange: string;
  languages: string;
  languageList: string[];
  addCandidateToEventUrl: string | null;
}

interface CandidateModalProps {
  modalTitle?: string;
  event?: any;
  searchUrl?: string | null;
  showSearch?: boolean;
}

const props = defineProps({
  listUrl: { default: null, type: String },
  showEvent: { default: true, type: Boolean },
  checkmarks: { default: false, type: Boolean },
})

const emit = defineEmits<{
  (e: "contentschanged"): void;
}>()

const searchCandidatesUrl = ref<string | null>(null)
const uploadCandidatesUrl = ref<string | null>(null)
const uploadTemplateUrl = ref<string | null>(null)
const candidateModalProps = ref<CandidateModalProps>({})
const candidatesListUrl = ref<string | null>(null)
const eventDetails = ref<EventDetails | null>(null)
const showTeams = ref(false)
const teamsListUrl = ref<string | null>(null)
const event = ref<any>(null)

const rootEl = useTemplateRef<HTMLElement>("rootEl")
const candidatesGrid = useTemplateRef<InstanceType<typeof Grid>>("candidatesGrid")
const teamsGrid = useTemplateRef<InstanceType<typeof TeamsGrid>>("teamsGrid")
const candidateModal = useTemplateRef<InstanceType<typeof CandidateModal>>("candidateModal")
const candidateDetailsModal = useTemplateRef<InstanceType<typeof CandidateDetailsModal>>("candidateDetailsModal")
const uploadCandidatesModal = useTemplateRef<InstanceType<typeof XlsxUploadModal>>("uploadCandidatesModal")
const selectOpenCandidatesModal = useTemplateRef<InstanceType<typeof RemoteModal>>("selectOpenCandidatesModal")

let ghostPopovers: Popover | null = null

onMounted(() => {
  if (rootEl.value) {
    ghostPopovers = new Popover(rootEl.value, {
      selector: '[data-bs-toggle="popover"]',
      title: (el) => el.getAttribute("data-bs-title") ?? "",
      content: (el) => el.getAttribute("data-bs-content") ?? "",
    })
  }
})

onBeforeUnmount(() => {
  rootEl.value?.querySelectorAll('[data-bs-toggle="popover"]').forEach((el) => {
    Popover.getInstance(el)?.dispose()
  })
  ghostPopovers?.dispose()
  ghostPopovers = null
})

const gridListUrl = computed((): string | undefined => {
  return candidatesListUrl.value || props.listUrl || undefined
})

const teamsListUrlProp = computed((): string | undefined => {
  return teamsListUrl.value || undefined
})

const candidateSearchUrl = computed((): string | undefined => {
  return candidateModalProps.value.searchUrl || undefined
})

const uploadCandidatesUrlProp = computed((): string | undefined => {
  return uploadCandidatesUrl.value || undefined
})

const uploadTemplateUrlProp = computed((): string | undefined => {
  return uploadTemplateUrl.value || undefined
})

const showEventDetails = computed((): boolean => {
  return props.showEvent && eventDetails.value != null
})

const hasAdditionalEventDetails = computed((): boolean => {
  return eventDetails.value != null && isNonEmptyString(eventDetails.value.detailsUrl) &&
    isNonEmptyString(eventDetails.value.detailsUrlHost)
})

const hasEventLanguages = computed((): boolean => {
  return eventDetails.value != null && isNonEmptyString(eventDetails.value.languages)
})

const gridLoadedLinks = (links: any): void => {
  searchCandidatesUrl.value = getValueStrNullable(links.searchCandidatesUrl)
  uploadTemplateUrl.value = getValueStrNullable(links.uploadTemplateUrl)
}

const gridMetaLoaded = (meta: any): void => {
  if (meta && meta.event) {
    const ev = meta.event
    if (typeof ev.name === "string" && ev.name.length > 0 &&
      typeof ev.url === "string" && ev.url.length > 0) {
      eventDetails.value = ev
    }
  } else {
    eventDetails.value = null
  }
}

const showNewCandidateModal = (newCandidateUrl: string): void => {
  candidateModalProps.value = {
    modalTitle: "Add candidate",
    searchUrl: searchCandidatesUrl.value,
    showSearch: typeof searchCandidatesUrl.value === "string" && searchCandidatesUrl.value.length > 0,
  }

  if (eventDetails.value) {
    candidateModalProps.value.event = {
      id: eventDetails.value.id,
      name: eventDetails.value.name,
      languages: eventDetails.value.languageList,
      addCandidateToEventUrl: eventDetails.value.addCandidateToEventUrl,
    }
  }

  nextTick(() => {
    candidateModal.value?.showModal(newCandidateUrl, event.value ? event.value.id : null)
  })
}

const showEditCandidateModal = (editUrl: string): void => {
  candidateModalProps.value = {
    modalTitle: "Edit candidate",
  }
  candidateModal.value?.showModal(`${editUrl}.json`, null)
}

const candidateModalSuccess = (): void => {
  candidateModal.value?.hideModal()
  candidatesGrid.value?.reload()
  emit("contentschanged")
}

const showUploadModal = (uploadUrl: string): void => {
  uploadCandidatesUrl.value = uploadUrl
  uploadCandidatesModal.value?.showModal(null)
}

const fetchContent = (data?: any): void => {
  let newListUrl: string | null = null
  if (data && typeof data.id === "string") {
    const parts = data.id.split(":")
    if (parts.length == 2) {
      if (parts[0] == "e" && data.candidatesUrl) {
        const eventId = parseInt(parts[1])
        if (!isNaN(eventId)) {
          newListUrl = data.candidatesUrl
        }
      } else if (parts[0] == "c" && data.candidatesUrl) {
        const candidateId = parseInt(parts[1])
        if (!isNaN(candidateId)) {
          newListUrl = data.candidatesUrl
        }
      } else if (parts[0] == "filter" && data.candidatesUrl) {
        newListUrl = data.candidatesUrl
      }
    }
  }

  showTeams.value = /\/teams/.test(newListUrl || "")
  if (showTeams.value) {
    teamsListUrl.value = newListUrl
    nextTick(() => {
      teamsGrid.value?.reload()
    })
  } else {
    candidatesListUrl.value = newListUrl
    nextTick(() => {
      candidatesGrid.value?.reload()
    })
  }
}

const approveSelectedCandidates = (approveSelectedUrl: string): void => {
  const selectedCandidateIds = candidatesGrid.value?.selectedItemIds

  if (selectedCandidateIds == null || (Array.isArray(selectedCandidateIds) && selectedCandidateIds.length === 0)) {
    alert("Please select at least one candidate")
    return
  }

  candidatesGrid.value?.showLoadingIndicator()

  if (isNonEmptyString(approveSelectedUrl)) {
    axios.post(approveSelectedUrl, {
      "selected_candidate_ids": selectedCandidateIds,
    }, {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": Rails.csrfToken(),
      },
    })
      .then((response) => {
        candidatesGrid.value?.hideLoadingIndicator()
        if (response.data && isPresent(response.data.error)) {
          candidatesGrid.value?.showError(response.data.error)
        } else {
          emit("contentschanged")
        }
      })
      .catch((error) => {
        candidatesGrid.value?.showError(error.message)
        candidatesGrid.value?.hideLoadingIndicator()
      })
  }
}

const approveSelectedOpenCandidates = (approveSelectedUrl: string): void => {
  const selectedCandidateIds = candidatesGrid.value?.selectedItemIds

  if (selectedCandidateIds == null || (Array.isArray(selectedCandidateIds) && selectedCandidateIds.length === 0)) {
    alert("Please select at least one candidate")
    return
  }

  selectOpenCandidatesModal.value?.showModal(
    approveSelectedUrl,
    { candidates_ids: selectedCandidateIds },
    { method: "POST" }
  )
}

const onApprovedOpenCandidates = (): void => {
  selectOpenCandidatesModal.value?.hideModal()
  candidatesGrid.value?.clearSelectedItemIds()
  emit("contentschanged")
}

const reload = (): void => {
  candidatesGrid.value?.reload()
}

const onHeaderAction = (
  actionOrName: string | GridHeaderAction | ButtonAction,
  _url?: string,
  action?: GridHeaderAction | ButtonAction
): void => {
  const headerAction = typeof actionOrName === "string" ? action : actionOrName
  if (headerAction == null) {
    return
  }

  if (headerAction.name == "approve-selected-candidates") {
    approveSelectedCandidates(headerAction.url)
  } else if (headerAction.name == "add-candidate") {
    showNewCandidateModal(headerAction.url)
  } else if (headerAction.name == "upload-candidates") {
    showUploadModal(headerAction.url)
  } else if (headerAction.name == "approve-selected-open-candidates") {
    approveSelectedOpenCandidates(headerAction.url)
  }
}

const onItemAction = (item: any, action: any): void => {
  if (action === "edit" || action.name === "edit") {
    showEditCandidateModal(item.links.edit)
  } else if (typeof action.type === "string") {
    if (action.type === "view-details" || action.name === "view-details") {
      candidateDetailsModal.value?.showModal(action.url)
    } else if (action.type == "action") {
      if (action.name == "unselect") {
        if (isNonEmptyString(action.url)) {
          if (confirm("Are you sure?")) {
            candidatesGrid.value?.performRequest(action.url, {}, { method: "POST" }, (_status, _response) => {
              emit("contentschanged")
            })
          }
        }
      }
    }
  }
}

const ghostDetails = (item: any): string => {
  if (item.meta && item.meta.ghost) {
    return `Deleted at: ${item.meta.ghost.deleted_at}<br>Deleted by: ${item.meta.ghost.deleted_by}<br><br><small class="text-muted"><strong>Ghost candidates</strong> are candidates that were deleted from OSP without cancelling all pending participations</small>`
  }
  return ""
}

const ibanStatus = (refundType: any, object: any): string => {
  return object.attributes.ministry_iban_status[refundType].title
}

const ibanStatusClass = (refundType: any, object: any): string => {
  const status = object.attributes.ministry_iban_status[refundType].status.toString()
  let className = ""
  if (status === "0") {
    // no reimbursement claimed
    className = "text-secondary"
  } else if (status === "1") {
    // other IBAN
    className = ""
  } else if (status === "2") {
    // ministry's IBAN
    className = "text-info"
  }
  return `glyphicons euro ${className}`
}

const gridRowClassFn = (item: any): string | null => {
  return item.meta && isPresent(item.meta.ghost) ? "table-warning" : null
}

const gridHeaderClassFn = (column: any): string | null => {
  const hc = candidatesGrid.value?.fHeaderClassBase(column) || ""
  if (column.name === "ncp_status_str" || column.name === "country_code" || column.name === "t_language_name") {
    return `tight ${hc}`
  }
  return hc
}

defineExpose({
  fetchContent,
  reload,
})
</script>
